import { useState, useEffect, useCallback } from 'react';
import { Scale, Droplets, Flame, Filter, ThermometerSun, Check, RotateCw, ListChecks, FlaskConical } from 'lucide-react';
import InfoBox from '../InfoBox';
import { FilterApparatus, AnalyticalBalance } from '@/components/lab';

const steps = [
  { id: 1, title: 'Weigh Sample', short: 'Weigh 0.2–0.4 g on analytical balance' },
  { id: 2, title: 'Transfer to Beaker', short: 'Quantitative transfer to clean beaker' },
  { id: 3, title: 'Dissolve in Water', short: 'Add 150 mL H₂O and stir' },
  { id: 4, title: 'Add Nitric Acid', short: 'Add 1 mL dilute HNO₃' },
  { id: 5, title: 'Add Silver Nitrate', short: 'Add AgNO₃ solution (precipitate forms)' },
  { id: 6, title: 'Digest Precipitate', short: 'Heat near boiling to coagulate' },
  { id: 7, title: 'Test Precipitation', short: 'Check supernatant is clear' },
  { id: 8, title: 'Filter Precipitate', short: 'Vacuum filtration through filter' },
  { id: 9, title: 'Wash Precipitate', short: 'H₂O then acetone washes' },
  { id: 10, title: 'Dry Precipitate', short: 'Dry at ~100°C to constant mass' },
  { id: 11, title: 'Calculate % Chloride', short: 'Use gravimetric factor GF = 0.2473' },
];

// Educational content for each step
const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Weighing the Sample',
    content: 'Accurate sample mass is critical in gravimetric analysis because all calculations are based on this value. The sample contains an unknown chloride (typically NaCl), and we will determine its percent chloride content.',
    whatHappens: 'A sample of 0.2-0.4g is weighed on an analytical balance to 4 decimal places (0.0001g precision).',
    observe: [
      'Use weighing by difference',
      'Record mass to 4 decimal places',
      'Sample should be dry',
      'Avoid moisture absorption'
    ]
  },
  2: {
    title: 'Transferring to Beaker',
    content: 'Quantitative transfer ensures all sample material ends up in the reaction beaker. Any sample lost during transfer will cause errors in the final percentage calculation (low results).',
    whatHappens: 'The sample is carefully transferred to a clean beaker, rinsing any adhered particles with distilled water.',
    observe: [
      'Rinse all powder into beaker',
      'Use distilled water for rinsing',
      'Beaker should be clean and dry initially',
      'No sample should remain in weighing vessel'
    ]
  },
  3: {
    title: 'Dissolving in Water',
    content: 'The chloride salt must be completely dissolved to ensure all chloride ions are available for precipitation. Water (150 mL) provides adequate volume for the precipitation reaction.',
    whatHappens: 'Water is added and the sample dissolves. Most chloride salts are highly soluble in water.',
    observe: [
      'Stir to aid dissolution',
      'Solution should be clear',
      'Check for undissolved particles',
      'Heat gently if needed'
    ]
  },
  4: {
    title: 'Adding Nitric Acid',
    content: 'Dilute HNO₃ acidifies the solution, which:\n1. Prevents precipitation of interfering substances (like AgOH)\n2. Dissolves any carbonate present (releasing CO₂)\n3. Creates conditions where only AgCl precipitates',
    whatHappens: 'A small amount of dilute nitric acid is added to ensure acidic conditions.',
    observe: [
      'Solution may effervesce (CO₂)',
      'pH becomes acidic',
      'Solution remains clear',
      'This prepares for precipitation'
    ]
  },
  5: {
    title: 'Adding Silver Nitrate',
    content: 'The precipitation reaction:\n\nAg⁺(aq) + Cl⁻(aq) → AgCl(s)\n\nSilver chloride is virtually insoluble (Ksp = 1.8 × 10⁻¹⁰). AgNO₃ is added in slight excess to ensure complete precipitation of chloride.',
    whatHappens: 'White, curdy AgCl precipitate forms immediately upon adding AgNO₃ solution.',
    observe: [
      'Immediate white precipitate',
      'Precipitate is curdy/cheese-like',
      'Add AgNO₃ slowly with stirring',
      'Continue until precipitation appears complete'
    ]
  },
  6: {
    title: 'Digesting the Precipitate',
    content: 'Heating (digestion) causes the small AgCl particles to coagulate into larger particles through Ostwald ripening. Larger particles are easier to filter and wash, and have less occluded impurities.',
    whatHappens: 'The mixture is heated near boiling for several minutes. The precipitate becomes more compact.',
    observe: [
      'Heat to near boiling (do not boil)',
      'Precipitate settles more easily',
      'Liquid above becomes clearer',
      'Stir occasionally during heating'
    ]
  },
  7: {
    title: 'Testing for Complete Precipitation',
    content: 'A few drops of AgNO₃ are added to the clear supernatant liquid. If no new precipitate forms, all chloride has been precipitated. If cloudiness appears, more AgNO₃ must be added.',
    whatHappens: 'The clear liquid above the precipitate is tested with additional AgNO₃ solution.',
    observe: [
      'Let precipitate settle first',
      'Add AgNO₃ to clear supernatant',
      'No cloudiness = complete precipitation',
      'If cloudy, add more AgNO₃'
    ]
  },
  8: {
    title: 'Filtering the Precipitate',
    content: 'Vacuum filtration through a sintered glass crucible or filter paper collects the AgCl precipitate. The crucible must be weighed before use (if using crucible) or the precipitate transferred to a pre-weighed vessel.',
    whatHappens: 'The mixture is poured through the filter. Vacuum helps draw the liquid through faster.',
    observe: [
      'Filter must be prepared properly',
      'Quantitatively transfer precipitate',
      'Rinse beaker into filter',
      'White precipitate collects on filter'
    ]
  },
  9: {
    title: 'Washing the Precipitate',
    content: 'Washing removes adsorbed impurities (excess AgNO₃, dissolved salts) from the precipitate. First wash with two or three 5 mL portions of distilled water to remove soluble impurities. Then wash with three 5 mL portions of acetone to displace water and speed drying. CAUTION: Acetone is highly flammable!',
    whatHappens: 'The precipitate is washed first with distilled water, then with acetone.',
    observe: [
      'Wash with 2-3 portions of 5 mL water',
      'Then 3 portions of 5 mL acetone',
      'Let each wash drain before adding more',
      'Acetone is flammable - no open flames'
    ]
  },
  10: {
    title: 'Drying the Precipitate',
    content: 'AgCl must be dried to remove wash water before weighing. It is dried at approximately 100°C in an oven on a numbered watch glass. Note: AgCl is light-sensitive and will darken (photo-decomposition) if exposed to bright light.',
    whatHappens: 'The precipitate is dried to constant mass in an oven at ~100°C.',
    observe: [
      'Protect from bright light',
      'Dry at ~100°C',
      'Cool in desiccator',
      'AgCl may darken slightly (normal)'
    ]
  },
  11: {
    title: 'Calculating Percent Chloride',
    content: 'The gravimetric factor (GF) relates the mass of precipitate to the mass of analyte:\n\nGF = (MW of Cl) / (MW of AgCl) = 35.453 / 143.32 = 0.2473\n\n%Cl = (mass AgCl × GF / mass sample) × 100',
    whatHappens: 'The mass of dried AgCl is used with the gravimetric factor to calculate percent chloride.',
    observe: [
      'GF for Cl from AgCl = 0.2473',
      'Use average mass if weighed twice',
      '%Cl = (mass AgCl × 0.2473 / sample mass) × 100',
      'Compare to expected value'
    ]
  }
};

interface Part1ChlorideProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

export default function Part1Chloride({ onStepComplete, currentStep = 1 }: Part1ChlorideProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // State management
  const [balanceTared, setBalanceTared] = useState(false);
  const [sampleMass, setSampleMass] = useState<number | null>(null);
  const [sampleTransferred, setSampleTransferred] = useState(false);
  const [sampleDissolved, setSampleDissolved] = useState(false);
  const [acidified, setAcidified] = useState(false);
  const [agno3Added, setAgno3Added] = useState(false);
  const [agno3Progress, setAgno3Progress] = useState(0);
  const [precipitateAmount, setPrecipitateAmount] = useState(0);
  const [isHeating, setIsHeating] = useState(false);
  const [digestComplete, setDigestComplete] = useState(false);
  const [precipitationTested, setPrecipitationTested] = useState(false);
  const [vacuumOn, setVacuumOn] = useState(false);
  const [filterProgress, setFilterProgress] = useState(0);
  const [washed, setWashed] = useState(false);
  const [dryingComplete, setDryingComplete] = useState(false);
  const [dryingProgress, setDryingProgress] = useState(0);
  const [precipitateMass, setPrecipitateMass] = useState<number | null>(null);
  const [secondMass, setSecondMass] = useState<number | null>(null);
  const [chloridePercent, setChloridePercent] = useState<number | null>(null);

  // AgNO3 addition simulation
  useEffect(() => {
    if (agno3Progress > 0 && agno3Progress < 100) {
      const interval = setInterval(() => {
        setAgno3Progress(prev => Math.min(prev + 2, 100));
        setPrecipitateAmount(prev => Math.min(prev + 2, 100));
      }, 100);
      return () => clearInterval(interval);
    }
    if (agno3Progress >= 100 && !agno3Added) {
      setAgno3Added(true);
      if (onStepComplete) onStepComplete('chloride-5');
    }
  }, [agno3Progress, agno3Added, onStepComplete]);

  // Heating/digestion simulation
  useEffect(() => {
    if (isHeating && !digestComplete) {
      const timeout = setTimeout(() => {
        setDigestComplete(true);
        setIsHeating(false);
        if (onStepComplete) onStepComplete('chloride-6');
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [isHeating, digestComplete, onStepComplete]);

  // Filtration simulation
  useEffect(() => {
    if (vacuumOn && filterProgress < 100) {
      const interval = setInterval(() => {
        setFilterProgress(prev => Math.min(prev + 1.5, 100));
      }, 100);
      return () => clearInterval(interval);
    }
  }, [vacuumOn, filterProgress]);

  // Drying simulation
  useEffect(() => {
    if (washed && dryingProgress > 0 && dryingProgress < 100) {
      const interval = setInterval(() => {
        setDryingProgress(prev => Math.min(prev + 1, 100));
      }, 100);
      return () => clearInterval(interval);
    }
    if (dryingProgress >= 100 && !dryingComplete) {
      setDryingComplete(true);
      if (onStepComplete) onStepComplete('chloride-10');
    }
  }, [washed, dryingProgress, dryingComplete, onStepComplete]);

  const handleTare = useCallback(() => {
    setBalanceTared(true);
  }, []);

  const handleWeighSample = useCallback(() => {
    if (balanceTared) {
      const mass = 0.28 + Math.random() * 0.04; // 0.28-0.32g
      setSampleMass(parseFloat(mass.toFixed(4)));
      if (onStepComplete) onStepComplete('chloride-1');
    }
  }, [balanceTared, onStepComplete]);

  const handleTransfer = useCallback(() => {
    if (sampleMass) {
      setSampleTransferred(true);
      if (onStepComplete) onStepComplete('chloride-2');
    }
  }, [sampleMass, onStepComplete]);

  const handleDissolve = useCallback(() => {
    if (sampleTransferred) {
      setSampleDissolved(true);
      if (onStepComplete) onStepComplete('chloride-3');
    }
  }, [sampleTransferred, onStepComplete]);

  const handleAcidify = useCallback(() => {
    if (sampleDissolved) {
      setAcidified(true);
      if (onStepComplete) onStepComplete('chloride-4');
    }
  }, [sampleDissolved, onStepComplete]);

  const handleAddAgNO3 = useCallback(() => {
    if (acidified) {
      setAgno3Progress(1);
    }
  }, [acidified]);

  const handleHeat = useCallback(() => {
    if (agno3Added) {
      setIsHeating(true);
    }
  }, [agno3Added]);

  const handleTestPrecipitation = useCallback(() => {
    if (digestComplete) {
      setPrecipitationTested(true);
      if (onStepComplete) onStepComplete('chloride-7');
    }
  }, [digestComplete, onStepComplete]);

  const handleStartFiltration = useCallback(() => {
    if (precipitationTested) {
      setVacuumOn(true);
      if (onStepComplete) onStepComplete('chloride-8');
    }
  }, [precipitationTested, onStepComplete]);

  const handleWash = useCallback(() => {
    if (filterProgress >= 100) {
      setWashed(true);
      if (onStepComplete) onStepComplete('chloride-9');
    }
  }, [filterProgress, onStepComplete]);

  const handleStartDrying = useCallback(() => {
    if (washed) {
      setDryingProgress(1);
    }
  }, [washed]);

  const handleWeighPrecipitate = useCallback(() => {
    if (dryingComplete && !precipitateMass) {
      const mass = (sampleMass || 0.3) * 1.2 + Math.random() * 0.02;
      setPrecipitateMass(parseFloat(mass.toFixed(4)));
    }
  }, [dryingComplete, sampleMass, precipitateMass]);

  const handleSecondWeigh = useCallback(() => {
    if (precipitateMass && !secondMass) {
      // Constant mass - very close to first measurement
      const mass = precipitateMass + (Math.random() - 0.5) * 0.0004;
      setSecondMass(parseFloat(mass.toFixed(4)));
    }
  }, [precipitateMass, secondMass]);

  const handleCalculate = useCallback(() => {
    if (secondMass && sampleMass) {
      // Gravimetric factor: Cl/AgCl = 35.453/143.32 = 0.2473
      const gf = 0.2473;
      const avgMass = (precipitateMass! + secondMass) / 2;
      const percentCl = (avgMass * gf / sampleMass) * 100;
      setChloridePercent(parseFloat(percentCl.toFixed(2)));
      if (onStepComplete) onStepComplete('chloride-11');
    }
  }, [secondMass, sampleMass, precipitateMass, onStepComplete]);

  // Derive step completion from state
  const stepDone = [
    !!sampleMass, sampleTransferred, sampleDissolved, acidified,
    agno3Added, digestComplete, precipitationTested,
    filterProgress >= 100, washed, dryingComplete, !!chloridePercent
  ];
  const completedCount = stepDone.filter(Boolean).length;
  // Current active step = first incomplete
  const activeStep = stepDone.indexOf(false) + 1 || steps.length;

  return (
    <div className="exp7-part-layout">
      {/* Steps Panel */}
      <div className="exp7-steps-panel">
        <div className="exp7-steps-header">
          <ListChecks size={16} />
          <h3>Part I: Chloride</h3>
        </div>
        <div className="exp7-steps-list">
          {steps.map(s => {
            const done = stepDone[s.id - 1];
            const active = s.id === activeStep;
            return (
              <div key={s.id} className={`exp7-step-item ${active && !done ? 'active' : ''}`}>
                <div className={`exp7-step-number ${done ? 'done' : active ? 'current' : ''}`}>
                  {done ? <Check size={12} /> : s.id}
                </div>
                <div className="exp7-step-content">
                  <div className="exp7-step-title-row">
                    <span className={`exp7-step-title ${active ? 'highlight' : ''}`}>{s.title}</span>
                    <button className="step-info-btn" onClick={(e) => { e.stopPropagation(); setShowInfo(s.id); }}>?</button>
                  </div>
                  {active && !done && <span className="exp7-step-hint">{s.short}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lab Bench */}
      <div className="exp7-lab-bench">
        <div className="exp7-bench-header">
          <div className="exp7-bench-title"><FlaskConical size={16} /><h3>Lab Bench</h3></div>
          <span className="exp7-bench-progress">{completedCount}/{steps.length} Complete</span>
        </div>
        <div className="exp7-bench-area">
      <div className="chloride-analysis-lab">
        {/* Equipment Row */}
        <div className="equipment-row">
          {/* Analytical Balance */}
          <div className="equipment-section">
            <AnalyticalBalance
              mass={sampleMass || 0}
              isTared={balanceTared}
              isStable={true}
              onTare={handleTare}
              onClick={!sampleMass ? handleWeighSample : undefined}
            />
            <span className="equipment-label">Analytical Balance</span>
          </div>

          {/* Reaction Beaker */}
          <div className="equipment-section">
            <svg viewBox="0 0 120 140" width={120} height={140}>
              <defs>
                <linearGradient id="beaker-glass-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                  <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                </linearGradient>
              </defs>

              {/* Beaker */}
              <path
                d="M20 20 L20 120 Q20 130 30 130 L90 130 Q100 130 100 120 L100 20"
                fill="url(#beaker-glass-grad)"
                stroke="#7A9BB8"
                strokeWidth="2"
              />

              {/* Solution */}
              {sampleDissolved && (
                <rect x="23" y="50" width="74" height="75" fill="#E6E6FA" opacity="0.6" rx="3" />
              )}

              {/* White AgCl precipitate */}
              {precipitateAmount > 0 && (
                <g>
                  {/* Curdy precipitate appearance */}
                  <ellipse
                    cx="60"
                    cy="100"
                    rx={precipitateAmount / 100 * 25}
                    ry={precipitateAmount / 100 * 10}
                    fill="#FFFFFF"
                    stroke="#DDDDDD"
                    strokeWidth="0.5"
                  />
                  {precipitateAmount > 50 && (
                    <>
                      <ellipse cx="50" cy="95" rx="8" ry="4" fill="#FFFAFA" />
                      <ellipse cx="70" cy="105" rx="10" ry="5" fill="#FFFAFA" />
                    </>
                  )}
                </g>
              )}

              {/* Heating indication */}
              {isHeating && (
                <g>
                  <rect x="15" y="132" width="90" height="8" fill="#FF5A36" rx="2" />
                  <circle cx="35" cy="125" r="2" fill="#FF5A36" opacity="0.5">
                    <animate attributeName="cy" values="125;115;125" dur="0.5s" repeatCount="indefinite" />
                  </circle>
                  <circle cx="60" cy="128" r="2" fill="#FF5A36" opacity="0.5">
                    <animate attributeName="cy" values="128;118;128" dur="0.6s" repeatCount="indefinite" />
                  </circle>
                  <circle cx="85" cy="126" r="2" fill="#FF5A36" opacity="0.5">
                    <animate attributeName="cy" values="126;116;126" dur="0.4s" repeatCount="indefinite" />
                  </circle>
                </g>
              )}
            </svg>
            <span className="equipment-label">Reaction Beaker</span>
          </div>

          {/* Vacuum Filtration */}
          <div className="equipment-section">
            <FilterApparatus
              vacuumOn={vacuumOn}
              filterProgress={filterProgress}
              filtrateFillLevel={filterProgress * 0.6}
              filtrateColor="#F5F5DC"
              precipitateAmount={filterProgress > 50 ? filterProgress : 0}
              precipitateColor="#FFFFFF"
              onClick={precipitationTested && !vacuumOn ? handleStartFiltration : undefined}
            />
          </div>
        </div>

        {/* Status and Progress */}
        <div className="status-section">
          {agno3Progress > 0 && agno3Progress < 100 && (
            <div className="progress-card">
              <Droplets size={16} />
              <span>Adding AgNO₃...</span>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${agno3Progress}%` }} />
              </div>
            </div>
          )}

          {isHeating && (
            <div className="progress-card heating">
              <Flame size={16} />
              <span>Digesting precipitate (coagulating)...</span>
            </div>
          )}

          {washed && dryingProgress > 0 && dryingProgress < 100 && (
            <div className="progress-card">
              <ThermometerSun size={16} />
              <span>Drying at ~100°C...</span>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${dryingProgress}%` }} />
              </div>
            </div>
          )}
        </div>

        {/* Data Table */}
        <div className="data-table">
          <h4>Gravimetric Data</h4>
          <table>
            <tbody>
              <tr>
                <td>Sample Mass</td>
                <td>{sampleMass ? `${sampleMass} g` : '-'}</td>
              </tr>
              <tr>
                <td>AgCl Mass (1st weighing)</td>
                <td>{precipitateMass ? `${precipitateMass} g` : '-'}</td>
              </tr>
              <tr>
                <td>AgCl Mass (2nd weighing)</td>
                <td>{secondMass ? `${secondMass} g` : '-'}</td>
              </tr>
              <tr className="result-row">
                <td>% Chloride</td>
                <td className="result-value">{chloridePercent ? `${chloridePercent}%` : '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          {!sampleMass && balanceTared && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleWeighSample}>
                <Scale size={16} />
                Weigh Sample (0.2-0.4g)
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(1)}>?</button>
            </div>
          )}
          {sampleMass && !sampleTransferred && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleTransfer}>
                Transfer to Beaker
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(2)}>?</button>
            </div>
          )}
          {sampleTransferred && !sampleDissolved && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleDissolve}>
                <Droplets size={16} />
                Add 150 mL H₂O
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(3)}>?</button>
            </div>
          )}
          {sampleDissolved && !acidified && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleAcidify}>
                Add 1 mL HNO₃
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(4)}>?</button>
            </div>
          )}
          {acidified && !agno3Added && agno3Progress === 0 && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleAddAgNO3}>
                <Droplets size={16} />
                Add AgNO₃ Solution
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(5)}>?</button>
            </div>
          )}
          {agno3Added && !digestComplete && !isHeating && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleHeat}>
                <Flame size={16} />
                Heat to Digest
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(6)}>?</button>
            </div>
          )}
          {digestComplete && !precipitationTested && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleTestPrecipitation}>
                Test Complete Precipitation
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(7)}>?</button>
            </div>
          )}
          {filterProgress >= 100 && !washed && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleWash}>
                <Droplets size={16} />
                Wash Precipitate
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(9)}>?</button>
            </div>
          )}
          {washed && dryingProgress === 0 && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleStartDrying}>
                <ThermometerSun size={16} />
                Dry at ~100°C
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(10)}>?</button>
            </div>
          )}
          {dryingComplete && !precipitateMass && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleWeighPrecipitate}>
                <Scale size={16} />
                Weigh AgCl (1st)
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(10)}>?</button>
            </div>
          )}
          {precipitateMass && !secondMass && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleSecondWeigh}>
                <RotateCw size={16} />
                Weigh AgCl (2nd)
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(10)}>?</button>
            </div>
          )}
          {secondMass && !chloridePercent && (
            <div className="action-btn-group">
              <button className="action-btn success" onClick={handleCalculate}>
                <Check size={16} />
                Calculate % Chloride
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(11)}>?</button>
            </div>
          )}
        </div>

        {/* Chemical equation */}
        <div className="equation-box">
          <span className="equation">Ag⁺(aq) + Cl⁻(aq) → AgCl(s) (white precipitate)</span>
          <span className="equation-note">Gravimetric Factor: Cl/AgCl = 35.453/143.32 = 0.2473</span>
        </div>

        <style>{`
          .exp7-part-layout {
            display: flex;
            gap: 16px;
            flex: 1;
            width: 100%;
            min-height: 0;
            padding: 16px;
          }
          .exp7-steps-panel {
            width: 240px;
            background: var(--bg-surface);
            border-radius: 12px;
            border: 1px solid var(--border);
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }
          .exp7-steps-header {
            background: linear-gradient(135deg, var(--primary), var(--primary-dark));
            padding: 14px 16px;
            display: flex;
            align-items: center;
            gap: 10px;
            color: white;
          }
          .exp7-steps-header h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp7-steps-list { flex: 1; overflow-y: auto; padding: 12px; }
          .exp7-step-item {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 8px 10px;
            border-radius: 8px;
            margin-bottom: 2px;
            transition: background 0.15s;
          }
          .exp7-step-item.active { background: rgba(255, 90, 54, 0.15); }
          .exp7-step-number {
            width: 24px; height: 24px; border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            font-size: 11px; font-weight: 600; flex-shrink: 0;
            background: var(--border); color: var(--text-muted);
          }
          .exp7-step-number.done { background: var(--secondary); color: white; }
          .exp7-step-number.current { background: var(--primary); color: white; }
          .exp7-step-content { flex: 1; min-width: 0; }
          .exp7-step-title-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
          .exp7-step-title { font-size: 12px; font-weight: 600; }
          .exp7-step-title.highlight { color: var(--primary-light); }
          .exp7-step-hint { font-size: 11px; color: var(--text-secondary); margin-top: 4px; display: block; }
          .exp7-lab-bench {
            flex: 1;
            background: var(--bg-surface);
            border-radius: 12px;
            border: 1px solid var(--border);
            display: flex;
            flex-direction: column;
            overflow: auto;
          }
          .exp7-bench-header {
            background: linear-gradient(135deg, var(--secondary), var(--secondary-dark));
            padding: 12px 16px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            color: white;
          }
          .exp7-bench-title { display: flex; align-items: center; gap: 10px; }
          .exp7-bench-title h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp7-bench-progress { font-size: 12px; opacity: 0.9; }
          .exp7-bench-area {
            flex: 1;
            padding: 20px;
            overflow-y: auto;
          }
          .chloride-analysis-lab {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 40px;
            flex-wrap: wrap;
            justify-content: center;
            align-items: flex-end;
          }

          .equipment-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .status-section {
            min-height: 50px;
          }

          .progress-card {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 20px;
            background: var(--bg-elevated);
            border-radius: 8px;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .progress-card.heating {
            background: rgba(255, 90, 54, 0.1);
            color: #FF5A36;
          }

          .progress-bar {
            width: 100px;
            height: 6px;
            background: var(--bg-dark);
            border-radius: 3px;
            overflow: hidden;
          }

          .progress-fill {
            height: 100%;
            background: #00A86B;
            transition: width 0.1s;
          }

          .data-table {
            background: var(--bg-elevated);
            padding: 16px;
            border-radius: 12px;
            width: 100%;
            max-width: 350px;
          }

          .data-table h4 {
            margin: 0 0 12px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .data-table table {
            width: 100%;
            font-size: 12px;
          }

          .data-table td {
            padding: 8px 4px;
            border-bottom: 1px solid var(--border);
          }

          .data-table td:first-child {
            color: var(--text-secondary);
          }

          .data-table td:last-child {
            text-align: right;
            font-family: monospace;
            color: var(--text-primary);
          }

          .result-row td {
            font-weight: 600;
            border-bottom: none;
          }

          .result-value {
            color: #00A86B !important;
            font-size: 14px !important;
          }

          .action-buttons {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            justify-content: center;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 20px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }

          .action-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .action-btn.success:hover {
            background: #008B5B;
          }

          .action-btn-group {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .equation-box {
            display: flex;
            flex-direction: column;
            gap: 4px;
            padding: 12px 20px;
            background: var(--bg-elevated);
            border-radius: 8px;
            text-align: center;
          }

          .equation {
            font-size: 13px;
            font-family: 'Times New Roman', serif;
            color: var(--text-primary);
          }

          .equation-note {
            font-size: 11px;
            color: var(--text-muted);
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp7"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
      </div>{/* exp7-bench-area */}
      </div>{/* exp7-lab-bench */}
    </div>
  );
}
